import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../components/webpage/Navbar";
import Footer from "../components/webpage/Footer";

/* ================= DISPLAY PRICES (legacy for VALUE only) =================
   We keep these so opt.value stays exactly like "STANDARD - 1 miesiąc"/"STANDARD - 1 rok".
*/
const licenseOptions = {
  STANDARD: [
    { label: "1 miesiąc", value: "STANDARD - 1 miesiąc", months: 1, price: "199 PLN" },
    { label: "1 rok",     value: "STANDARD - 1 rok",     years: 1, price: "2388 PLN" },
  ],
  PRO: [
    { label: "1 miesiąc", value: "PRO - 1 miesiąc", months: 1, price: "299 PLN" },
    { label: "1 rok",     value: "PRO - 1 rok",     years: 1, price: "3588 PLN" },
  ],
  ENTERPRISE: [
    { label: "1 miesiąc", value: "ENTERPRISE - 1 miesiąc", months: 1, price: "499 PLN" },
    { label: "1 rok",     value: "ENTERPRISE - 1 rok",     years: 1, price: "5988 PLN" },
  ],
  KONTRAHENT: [],
};

/* ================= PLAN MAPPING ================= */
const planMap = {
  BASIC: "STANDARD",
  PROFESSIONAL: "PRO",
  PREMIUM: "ENTERPRISE",
  PRO: "PRO",
  ENTERPRISE: "ENTERPRISE",
  KONTRAHENT: "KONTRAHENT",
  "PAKIET-KONTRAHENT": "KONTRAHENT",
};

const planLabels = {
  BASIC: "Basic",
  PROFESSIONAL: "Professional",
  PREMIUM: "Enterprise",
  PRO: "Professional",
  ENTERPRISE: "Enterprise",
  KONTRAHENT: "Pakiet Kontrahenta",
  "PAKIET-KONTRAHENT": "Pakiet Kontrahenta",
};

/* ================= STRIPE PRICE IDS ================= */
const stripePrices = {
  STANDARD: {
    monthly: { id: "price_1SmqhrIDfMKxcVPLjntcHGjM", label: "199 PLN / miesiąc" },
    yearly:  { id: "price_1SmGPfIDfMKxcVPL5kyDyqP5", label: "1910 PLN / rok" },
  },
  PRO: {
    monthly: { id: "price_1SmGNTIDfMKxcVPL1ZXXzTuz", label: "299 PLN / miesiąc" },
    yearly:  { id: "price_1SmGQTIDfMKxcVPLWSHI9JVJ", label: "2870 PLN / rok" },
  },
  ENTERPRISE: {
    monthly: { id: "price_1SmGO4IDfMKxcVPLmnURlizB", label: "499 PLN / miesiąc" },
    yearly:  { id: "price_1SmGVSIDfMKxcVPLLsytHFsL", label: "4790 PLN / rok" },
  },
  KONTRAHENT: {},
};

// helper: "1910 PLN / rok" -> "1910.00"
function parseStripeLabelToNumericString(label) {
  if (!label) return null;
  const match = label.match(/([\d.,]+)/);
  if (!match) return null;
  const num = Number(match[1].replace(/\./g, "").replace(",", "."));
  return Number.isFinite(num) ? num.toFixed(2) : null;
}

const SelectLicensePage = () => {
  const [params] = useSearchParams();
  const selectedPlanRaw = params.get("plan") || "BASIC";
  const selectedPlan = selectedPlanRaw?.toUpperCase();
  const licenseTier = planMap[selectedPlan] || "STANDARD";
  const displayLabel = planLabels[selectedPlan] || selectedPlan;

  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.4 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const onChoose = (opt) => {
    const interval = opt.years === 1 ? "yearly" : "monthly";
    const stripeCfg = stripePrices[licenseTier]?.[interval];

    if (!stripeCfg?.id) {
      alert("Brak skonfigurowanej ceny Stripe dla tej opcji.");
      return;
    }

    // IMPORTANT: keep value EXACT for login control
    const license = {
      tier: licenseTier,
      planParam: selectedPlan,
      label: opt.label,
      value: opt.value,              // e.g. "ENTERPRISE - 1 rok"
      interval,
      priceId: stripeCfg.id,
      displayPrice: stripeCfg.label, // "499 PLN / miesiąc"
      orderPrice: parseStripeLabelToNumericString(stripeCfg.label),
    };

    navigate("/order-summary", { state: { license } });
  };

  const options = licenseOptions[licenseTier] || [];
  const hasStripe =
    (stripePrices[licenseTier] && stripePrices[licenseTier].monthly) ||
    (stripePrices[licenseTier] && stripePrices[licenseTier].yearly);

  return (
    <>
      <Navbar />
      <PlansContainer ref={containerRef} className={isVisible ? "visible" : ""}>
        <Header>
          Wybierz subskrypcję dla: <DisplayLabel>{displayLabel}</DisplayLabel>
        </Header>

        {!hasStripe && (
          <InfoBox>
            Dla wybranego planu ({displayLabel}) nie skonfigurowano subskrypcji Stripe.
            Skontaktuj się z nami.
          </InfoBox>
        )}

        <PlansWrapper>
          {options.map((opt, idx) => {
            const isYear = opt.years === 1;
            const interval = isYear ? "yearly" : "monthly";
            const stripeCfg = stripePrices[licenseTier]?.[interval];

            if (!stripeCfg) return null;

            return (
              <PlanCard
                key={idx}
                $highlighted={isYear}
                className={isVisible ? "fade-in" : ""}
                onClick={() => onChoose(opt)}
              >
                {isYear && <Popular>Najpopularniejsze</Popular>}
                {isYear && <DiscountRibbon>-20%</DiscountRibbon>}

                <PlanHeader>{isYear ? "ROCZNA" : "MIESIĘCZNA"}</PlanHeader>

                <PlanPrice>{stripeCfg.label}</PlanPrice>

                <PlanDescription>
                  {isYear
                    ? "Zapłać raz na rok i oszczędzaj względem miesięcznej płatności. Subskrypcję możesz anulować w dowolnym momencie – dostęp zachowasz do końca opłaconego roku."
                    : "Rozliczanie co miesiąc. Subskrypcję możesz anulować w dowolnym momencie – dostęp zachowasz do ostatniego dnia bieżącego okresu rozliczeniowego."}
                </PlanDescription>

                <PlanButton $highlighted={isYear}>Wybierz</PlanButton>
              </PlanCard>
            );
          })}
        </PlansWrapper>

        {/* 🔔 Global note under plans */}
        <CancellationNote>
          Subskrypcję możesz anulować w dowolnym momencie – po rezygnacji zachowasz
          dostęp do systemu aż do ostatniego dnia opłaconego okresu rozliczeniowego.
        </CancellationNote>
      </PlansContainer>
      <Footer />
    </>
  );
};

export default SelectLicensePage;

/* ================= styles ================= */

const PlansContainer = styled.div`
  text-align: center;
  padding: 2rem 1rem 5rem;
  background: url("/back.jpg") no-repeat center center fixed;
  background-size: cover;
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.8);
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  min-height: 80vh;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DisplayLabel = styled.span`
  display: inline-block;
  font-weight: 800;
  background: linear-gradient(to right, rgb(60, 207, 233), rgb(95, 92, 82));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
  margin-left: 0.3rem;
`;

const Header = styled.h2`
  font-size: 2.2rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 2.5rem;
`;

const PlansWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
`;

const PlanCard = styled.div`
  position: relative;
  flex: 1;
  max-width: 380px;
  width: 100%;
  background: ${({ $highlighted }) =>
    $highlighted ? "linear-gradient(to bottom, #fbbf24, #fde68a)" : "white"};
  border: 2px solid ${({ $highlighted }) => ($highlighted ? "#059669" : "#d1d5db")};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${({ $highlighted }) =>
    $highlighted ? "0 4px 20px rgba(16, 185, 129, 0.4)" : "none"};
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;

  &.fade-in { opacity: 1; transform: translateY(0); }

  &:hover {
    border-color: ${({ $highlighted }) => ($highlighted ? "#047857" : "#6366f1")};
    transform: scale(1.03);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 1024px) { max-width: 90%; }
  @media (max-width: 768px)  { max-width: 80%; padding: 1.5rem; }
`;

const Popular = styled.div`
  position: absolute;
  top: -12px;
  right: 16px;
  background: #10b981;
  color: white;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 999px;
`;

const DiscountRibbon = styled.div`
  position: absolute;
  top: 14px;
  left: -40px;
  transform: rotate(-45deg);
  background: #ef4444;
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 6px 48px;
  font-size: 0.9rem;
  box-shadow: 0 6px 12px rgba(0,0,0,0.15);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 100%;
    border: 8px solid transparent;
    border-top-color: #b91c1c;
  }
  &::before { left: 12px; }
  &::after  { right: 12px; }
`;

const PlanHeader = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4b5563;
  margin-bottom: 0.75rem;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) { font-size: 1.6rem; }
`;

const PlanDescription = styled.div`
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const PlanButton = styled.button`
  background: ${({ $highlighted }) => ($highlighted ? "#059669" : "#3bc3cc")};
  color: white;
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $highlighted }) => ($highlighted ? "#047857" : "black")};
    transform: scale(1.08);
  }
`;

const InfoBox = styled.div`
  margin: 0 auto 1.5rem;
  padding: 1rem 1.25rem;
  max-width: 720px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  color: #334155;
`;

const CancellationNote = styled.p`
  margin: 2.5rem auto 0;
  max-width: 720px;
  font-size: 0.9rem;
  color: #4b5563;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  padding: 0.75rem 1.5rem;
  border: 1px dashed #d1d5db;
`;
